import React, { useMemo } from "react";
import { Table, TableBody, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as Styled from "./styles";
import { getProviderCapabilities } from "../../utils/providerHelpers";
import { BASIC_INFO_FIELDS, getCredentialLabel } from "./reviewConfig";
import { isConstSchemaProperty } from "../../utils/schemaConstFields";
import type { AvailableProvider } from "../../types";

export interface ProviderData {
  state: string;
  name: string;
  code: string;
  priority: number;
  methods: string[];
  credentials: Record<string, string>;
  acquirer?: {
    merchantId?: string;
    brands?: string[];
    creditBrands?: string[];
    debitBrands?: string[];
  };
}

interface BasicInformationSectionProps {
  provider: ProviderData;
  mcc: string;
  hidePriority?: boolean;
  hideMerchantId?: boolean;
  hideMcc?: boolean;
}

interface CredentialsSectionProps {
  provider: ProviderData;
  selectedProvider: AvailableProvider | null;
}

interface FlagsSectionProps {
  brands: string[];
}

export interface ReviewProviderProps {
  provider: ProviderData;
  mcc?: string;
  selectedProvider?: AvailableProvider | null;
}

const FIELDS_PER_ROW = 2;

function formatStateDisplay(state: string): string {
  return state === "Enabled" ? "Ativo" : "Inativo";
}

function BasicInformationSection({
  provider,
  mcc,
  hidePriority = false,
  hideMerchantId = false,
  hideMcc = false,
}: BasicInformationSectionProps) {
  const { t } = useTranslation();

  const visibleFields = BASIC_INFO_FIELDS.filter((field) => {
    if (field.showWhen === "whenNotPagaleve" && hidePriority) return false;
    if (field.id === "merchantId" && hideMerchantId) return false;
    if (field.id === "mcc" && (hideMerchantId || hideMcc)) return false;
    return true;
  });

  const rows: (typeof visibleFields)[] = [];
  for (let i = 0; i < visibleFields.length; i += FIELDS_PER_ROW) {
    rows.push(visibleFields.slice(i, i + FIELDS_PER_ROW));
  }

  return (
    <Styled.SectionContainer>
      <Styled.SectionTitle variant="body1">
        {t("basicInformation")}
      </Styled.SectionTitle>

      {rows.map((rowFields, rowIndex) => (
        <Styled.FormRow key={`row-${rowIndex}`}>
          {rowFields.map((field) => {
            const value = field.getValue(
              provider as unknown as Record<string, unknown>,
              mcc,
            );
            const displayValue =
              field.id === "state" && typeof value === "string"
                ? formatStateDisplay(value)
                : String(value ?? "");
            const label = field.label ?? t(field.labelKey);

            return (
              <Styled.StyledFormControl key={field.id}>
                <Styled.StyledTextField
                  label={label}
                  value={displayValue}
                  disabled
                />
              </Styled.StyledFormControl>
            );
          })}
        </Styled.FormRow>
      ))}
    </Styled.SectionContainer>
  );
}

function getCredentialTitleFromSchema(
  selectedProvider: AvailableProvider | null,
  key: string,
): string | undefined {
  const prop =
    selectedProvider?.schema?.properties?.credentials?.properties?.[key];
  const title =
    prop && typeof prop === "object" && "title" in prop
      ? (prop as { title?: string }).title
      : undefined;
  return typeof title === "string" ? title : undefined;
}

function CredentialsSection({
  provider,
  selectedProvider,
}: CredentialsSectionProps) {
  const { t } = useTranslation();
  const credentials = provider.credentials ?? {};
  const entries = Object.entries(credentials).filter(([key, value]) => {
    if (key === "type" || value == null || value === "") {
      return false;
    }
    const property =
      selectedProvider?.schema?.properties?.credentials?.properties?.[key];
    return !isConstSchemaProperty(property);
  });

  if (entries.length === 0) return null;

  return (
    <Styled.CredentialsSectionContainer>
      <Styled.SectionTitle variant="body1">
        {t("credentials")}
      </Styled.SectionTitle>

      <Styled.CredentialsFieldsContainer>
        {entries.map(([key, value]) => {
          const titleFromSchema = getCredentialTitleFromSchema(
            selectedProvider,
            key,
          );
          const label = getCredentialLabel(key, titleFromSchema);
          return (
            <Styled.StyledTextFieldFullWidth
              key={key}
              label={`${label}*`}
              value={value}
              disabled
              fullWidth
            />
          );
        })}
      </Styled.CredentialsFieldsContainer>
    </Styled.CredentialsSectionContainer>
  );
}

function FlagsSection({ brands }: FlagsSectionProps) {
  const { t } = useTranslation();

  if (brands.length === 0) return null;

  return (
    <Styled.FlagsSectionContainer>
      <Styled.SectionTitle variant="body1">{t("flags")}</Styled.SectionTitle>

      <Styled.TableContainerStyled>
        <Table>
          <Styled.StyledTableHead>
            <TableRow>
              <Styled.StyledTableCellHeader>
                <Styled.StyledTableCellHeaderText>
                  {t("flag")}
                </Styled.StyledTableCellHeaderText>
              </Styled.StyledTableCellHeader>
            </TableRow>
          </Styled.StyledTableHead>
          <TableBody>
            {brands.map((row, index) => (
              <TableRow key={`${row}-${index}`}>
                <Styled.StyledTableCellBody>
                  <Styled.StyledTableCellBodyText>
                    {row}
                  </Styled.StyledTableCellBodyText>
                </Styled.StyledTableCellBody>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Styled.TableContainerStyled>
    </Styled.FlagsSectionContainer>
  );
}

const ReviewProvider: React.FC<ReviewProviderProps> = ({
  provider,
  mcc = "",
  selectedProvider = null,
}) => {
  const capabilities = useMemo(
    () => getProviderCapabilities(selectedProvider),
    [selectedProvider],
  );

  const brands = provider.acquirer?.brands ?? [];
  const showBrands = capabilities.hasBrands && brands.length > 0;

  return (
    <Styled.ContentWrapper>
      <BasicInformationSection
        provider={provider}
        mcc={mcc}
        hidePriority={!capabilities.isPriorityEditable}
        hideMerchantId={!capabilities.hasMerchantId}
        hideMcc={!capabilities.showMcc}
      />

      <Styled.DividerStyled />

      <CredentialsSection
        provider={provider}
        selectedProvider={selectedProvider}
      />

      {showBrands && (
        <>
          <Styled.DividerStyled />
          <FlagsSection brands={brands} />
        </>
      )}
    </Styled.ContentWrapper>
  );
};

export default ReviewProvider;
